import { useEffect, useRef } from "react";
import * as Immutable from "immutable";
import URI from "urijs";
import View from "views/logic/views/View";
import { filtersToStreamSet, filtersToStreamCategorySet } from "views/logic/queries/Query";
import { isTypeRelativeWithStartOnly, isTypeRelativeWithEnd } from "views/typeGuards/timeRange";
import useViewType from "views/hooks/useViewType";
import useCurrentQuery from "views/logic/queries/useCurrentQuery";
import useGlobalOverride from "views/hooks/useGlobalOverride";
import useHistory from "routing/useHistory";
const TIMERANGE_PARAMS = ["from", "to", "keyword", "relative", "rangetype"];
const extractTimerangeParams = (timerange) => {
  const { type } = timerange;
  const result = { rangetype: type };
  const formatResult = (paramsObject) => Object.entries(paramsObject);
  switch (timerange.type) {
    case "relative":
      if (isTypeRelativeWithStartOnly(timerange)) {
        return formatResult({ ...result, relative: timerange.range });
      }
      if (isTypeRelativeWithEnd(timerange)) {
        if ("to" in timerange) {
          return formatResult({ ...result, from: timerange.from, to: timerange.to });
        }
        return formatResult({ ...result, from: timerange.from });
      }
      return formatResult(result);
    case "keyword":
      return formatResult({ ...result, keyword: timerange.keyword });
    case "absolute":
      return formatResult({ ...result, from: timerange.from, to: timerange.to });
    default:
      return Object.entries(result);
  }
};
const uriForSearchView = (query, searchQuery) => {
  const {
    query: { query_string: queryString },
    timerange,
    filter = Immutable.Map()
  } = searchQuery;
  const baseUri = new URI(query).setSearch("q", queryString).removeQuery("from").removeQuery("to").removeQuery("keyword").removeQuery("relative");
  const uriWithTimerange = extractTimerangeParams(timerange).reduce(
    (prev, [key, value]) => prev.setSearch(key, String(value)),
    baseUri
  );
  const currentStreams = filtersToStreamSet(filter);
  const currentStreamCategories = filtersToStreamCategorySet(filter);
  const uriWithStreams = currentStreams.isEmpty() ? uriWithTimerange.removeSearch("streams") : uriWithTimerange.setSearch("streams", currentStreams.join(","));
  const uri = currentStreamCategories.isEmpty() ? uriWithStreams.removeSearch("stream_categories") : uriWithStreams.setSearch("stream_categories", currentStreamCategories.join(","));
  return uri.toString();
};
const uriForDashboardView = (query, globalOverride) => {
  var _a;
  const baseUri = TIMERANGE_PARAMS.reduce((acc, param) => acc.removeQuery(param), new URI(query)).removeQuery("q");
  if (!globalOverride) {
    return baseUri.toString();
  }
  const queryString = (_a = globalOverride.query) == null ? void 0 : _a.query_string;
  const { timerange } = globalOverride;
  let uri = baseUri;
  if (queryString) {
    uri = uri.setSearch("q", queryString);
  }
  if (timerange) {
    uri = extractTimerangeParams(timerange).reduce((acc, [key, value]) => acc.setSearch(key, String(value)), uri);
  }
  return uri.toString();
};
const uriForView = (viewType, query, searchQuery, globalOverride) => {
  if (viewType === View.Type.Search) {
    return searchQuery ? uriForSearchView(query, searchQuery) : void 0;
  }
  if (viewType === View.Type.Dashboard) {
    return uriForDashboardView(query, globalOverride);
  }
  return void 0;
};
function canonicalizeUri(uri) {
  const [path, queryString = ""] = uri.split("?", 2);
  if (!queryString) return path;
  const params = queryString.split("&").filter(Boolean).map((part) => {
    const [k, v = ""] = part.split("=", 2);
    return [k, v];
  }).sort(([k1, v1], [k2, v2]) => k1 === k2 ? v1.localeCompare(v2) : k1.localeCompare(k2));
  const canonicalQuery = params.map(([k, v]) => `${k}=${v}`).join("&");
  return `${path}?${canonicalQuery}`;
}
function isSameUri(a, b) {
  return canonicalizeUri(a) === canonicalizeUri(b);
}
const useSyncWithQueryParameters = (currentUri) => {
  const viewType = useViewType();
  const currentQuery = useCurrentQuery();
  const globalOverride = useGlobalOverride();
  const history = useHistory();
  const lastSyncedUriRef = useRef(currentUri);
  const isFirstSyncRef = useRef(true);
  useEffect(() => {
    const uriForCurrentView = uriForView(viewType, currentUri, currentQuery, globalOverride);
    if (!uriForCurrentView) {
      return;
    }
    const currentUriMatchesCurrentView = isSameUri(currentUri, uriForCurrentView);
    const currentUriMatchesLastSync = isSameUri(currentUri, lastSyncedUriRef.current);
    if (!currentUriMatchesLastSync && !currentUriMatchesCurrentView) {
      return;
    }
    if (!currentUriMatchesCurrentView) {
      const updateHistory = isFirstSyncRef.current ? history.replace : history.push;
      updateHistory(uriForCurrentView);
    }
    isFirstSyncRef.current = false;
    lastSyncedUriRef.current = uriForCurrentView;
  }, [currentQuery, globalOverride, history, currentUri, viewType]);
};
export default useSyncWithQueryParameters;
